import * as React from "react";
import {useTranslation} from "react-i18next";
import {Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import {ExternalApiCityOverride, ExternalApiClient} from "../../../../model/ExternalApiClient";
import {useCodeBookDetailContextNew} from "../../../../raal_components/controller/CodeBookDetail";

/**
 * Inline editor pro pole "cityOverrides" v rámci ExternalApiClientForm.
 * Vykresluje editovatelnou tabulku "cityIn" -> "cityOut" + přidat/smazat řádek.
 *
 * Sahá na data přes projektový CodeBookDetailContextNew (ne react-final-form),
 * protože tento Form je vlastní wrapper a useForm by uvnitř nenašel parent.
 */
export const ExternalApiCityOverridesEditor: React.FC = () => {
    const {t} = useTranslation();
    const ctx = useCodeBookDetailContextNew<ExternalApiClient>();
    const data = ctx?.data;
    // Defenzivně - backend může (zejména u LAZY Hibernate kolekce) vrátit jiný typ než array.
    const overrides: ExternalApiCityOverride[] = Array.isArray(data?.cityOverrides) ? data!.cityOverrides : [];

    const replace = (next: ExternalApiCityOverride[]) => {
        if (!data) return;
        ctx.setData({...data, cityOverrides: next} as ExternalApiClient);
    };

    const updateRow = (idx: number, field: keyof ExternalApiCityOverride, val: string) => {
        const next = [...overrides];
        const row = {...(next[idx] ?? new ExternalApiCityOverride())};
        (row as any)[field] = val;
        next[idx] = row;
        replace(next);
    };

    const addRow = () => replace([...overrides, new ExternalApiCityOverride()]);
    const deleteRow = (idx: number) => replace(overrides.filter((_, i) => i !== idx));

    return (
        <div>
            <h4>{t("ExternalApiClient.CityOverrides")}</h4>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t("ExternalApiClient.cityIn")}</TableCell>
                        <TableCell>{t("ExternalApiClient.cityOut")}</TableCell>
                        <TableCell width={48}/>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {overrides.map((o, idx) => (
                        <TableRow key={idx}>
                            <TableCell>
                                <TextField
                                    value={o.cityIn ?? ""}
                                    onChange={e => updateRow(idx, "cityIn", e.target.value.toUpperCase())}
                                    inputProps={{maxLength: 128}}
                                    fullWidth
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    value={o.cityOut ?? ""}
                                    onChange={e => updateRow(idx, "cityOut", e.target.value)}
                                    inputProps={{maxLength: 128}}
                                    fullWidth
                                />
                            </TableCell>
                            <TableCell>
                                <IconButton size="small" onClick={() => deleteRow(idx)} aria-label={t("Default.Remove")}>
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Button onClick={addRow} startIcon={<AddIcon/>} size="small" style={{marginTop: 8}}>
                {t("ExternalApiClient.AddCityOverride")}
            </Button>
        </div>
    );
};
